import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FiPaperclip, FiUpload, FiFile, FiImage, FiDownload, FiExternalLink, FiTrash2, FiX, FiEdit2, FiCheck, FiMail } from 'react-icons/fi';
import { API_URL, API_BASE_URL, MAX_UPLOAD_BYTES } from '../config';
import { useAuth } from '../contexts/AuthContext';
import EmailToClientModal from './EmailToClientModal';

const MAX_BYTES = MAX_UPLOAD_BYTES;

const fmtBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (mime, name) => {
  if (mime && mime.startsWith('image/')) return true;
  const ext = (name || '').split('.').pop()?.toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'tif', 'tiff'].includes(ext);
};

const isPdf = (mime, name) => {
  if (mime === 'application/pdf') return true;
  return (name || '').toLowerCase().endsWith('.pdf');
};

/**
 * Lists and manages file attachments for a job (PDFs, plans, photos, DWG, etc.).
 * Designed to drop into both desktop JobDetail and the mobile job view.
 * - Anyone can upload.
 * - Only the uploader or an admin can edit caption.
 * - Only admins can delete (per project decision).
 */
function JobAttachments({ jobId, compact = false, phases = [] }) {
  const { user } = useAuth();
  const isAdmin = user?.can_manage_payroll;
  const hasPhases = Array.isArray(phases) && phases.length > 0;
  // Map a doc's phase_tag (a phase id as a string, or 'shared'/null) to a display label.
  const phaseLabel = (tag) => {
    if (!tag || tag === 'shared') return 'Shared';
    const p = phases.find(ph => String(ph.id) === String(tag));
    return p ? (p.name || p.kind) : 'Shared';
  };
  const phaseKind = (tag) => {
    const p = phases.find(ph => String(ph.id) === String(tag));
    return p ? p.kind : null;
  };
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingCaption, setPendingCaption] = useState('');
  const [pendingPhaseTag, setPendingPhaseTag] = useState('');  // '' = Shared / all phases
  const [pendingFile, setPendingFile] = useState(null);
  const [editingCaptionId, setEditingCaptionId] = useState(null);
  const [editingCaptionVal, setEditingCaptionVal] = useState('');
  // Email to client
  const [showEmail, setShowEmail] = useState(false);

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/jobs/${jobId}/attachments`, config);
      setAttachments(res.data?.attachments || []);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      alert(`File is too large (${fmtBytes(file.size)}). Maximum is ${fmtBytes(MAX_BYTES)}.`);
      e.target.value = '';
      return;
    }
    setPendingFile(file);
    setPendingCaption('');
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      if (pendingCaption.trim()) fd.append('caption', pendingCaption.trim());
      if (hasPhases && pendingPhaseTag) fd.append('phase_tag', pendingPhaseTag);
      await axios.post(`${API_URL}/jobs/${jobId}/attachments`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
      setPendingFile(null);
      setPendingCaption('');
      setPendingPhaseTag('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      alert('Upload failed: ' + msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancelPending = () => {
    setPendingFile(null);
    setPendingCaption('');
    setPendingPhaseTag('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const changePhase = async (att, tag) => {
    try {
      await axios.patch(`${API_URL}/jobs/${jobId}/attachments/${att.id}`, { phase_tag: tag || 'shared' }, config);
      load();
    } catch (err) {
      alert('Failed to update phase: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (att) => {
    if (!isAdmin) {
      alert('Only admins can delete attachments. Ask the office to remove this.');
      return;
    }
    if (!window.confirm(`Delete "${att.original_filename || 'this attachment'}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/jobs/${jobId}/attachments/${att.id}`, config);
      load();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const openView = (att) => {
    // PDFs/images render inline; everything else falls through to download. Token in query
    // string so the new tab can authenticate (same pattern as quote/letter PDFs).
    // view_url already starts with /api/... so use API_BASE_URL (the host without /api).
    const url = `${API_BASE_URL || ''}${att.view_url}?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  const handleDownload = (att) => {
    const url = `${API_BASE_URL || ''}${att.download_url}?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  const startEditCaption = (att) => {
    setEditingCaptionId(att.id);
    setEditingCaptionVal(att.caption || '');
  };

  const saveCaption = async (att) => {
    try {
      await axios.patch(`${API_URL}/jobs/${jobId}/attachments/${att.id}`, { caption: editingCaptionVal }, config);
      setEditingCaptionId(null);
      load();
    } catch (err) {
      alert('Failed to update caption: ' + (err.response?.data?.error || err.message));
    }
  };

  const canEditCaption = (att) => att.uploaded_by_id === user?.id || isAdmin;

  const toggleClientVisible = async (att) => {
    try {
      await axios.patch(`${API_URL}/jobs/${jobId}/attachments/${att.id}`, { client_visible: !att.client_visible }, config);
      load();
    } catch (err) {
      alert('Failed to update sharing: ' + (err.response?.data?.error || err.message));
    }
  };

  // Separate from client sharing on purpose: a doc shared with the property owner is NOT
  // automatically visible to outside subcontractors (engineers, architects).
  const toggleCollaboratorVisible = async (att) => {
    try {
      await axios.patch(`${API_URL}/jobs/${jobId}/attachments/${att.id}`, { collaborator_visible: !att.collaborator_visible }, config);
      load();
    } catch (err) {
      alert('Failed to update sharing: ' + (err.response?.data?.error || err.message));
    }
  };

  const iconFor = (att) => {
    if (isImage(att.mime_type, att.original_filename)) return <FiImage size={compact ? 18 : 22} style={{ color: '#16a34a' }} />;
    if (isPdf(att.mime_type, att.original_filename)) return <FiFile size={compact ? 18 : 22} style={{ color: '#dc2626' }} />;
    return <FiFile size={compact ? 18 : 22} style={{ color: '#6b7280' }} />;
  };

  return (
    <div style={{
      padding: compact ? '12px' : '1.5rem',
      background: 'white',
      borderRadius: '8px',
      boxShadow: compact ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
      border: compact ? '1px solid #e5e7eb' : 'none',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#2563eb', fontSize: compact ? '15px' : '18px' }}>
          <FiPaperclip /> Attachments
          {attachments.length > 0 && (
            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>({attachments.length})</span>
          )}
        </h2>
        <button
          onClick={handlePickFile}
          disabled={uploading}
          style={{
            padding: '8px 14px', backgroundColor: '#2563eb', color: 'white',
            border: 'none', borderRadius: '6px', cursor: uploading ? 'wait' : 'pointer',
            fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <FiUpload size={14} /> Add File
        </button>
        {attachments.length > 0 && (
          <button
            onClick={() => setShowEmail(true)}
            style={{
              padding: '8px 14px', backgroundColor: '#16a34a', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <FiMail size={14} /> Email to Client
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.tif,.tiff,.dwg,.dxf,.dgn,.kml,.kmz,.gpx,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.zip"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Pending upload card */}
      {pendingFile && (
        <div style={{
          padding: '12px', marginBottom: '12px', backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe', borderRadius: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FiFile size={18} style={{ color: '#2563eb' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pendingFile.name}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{fmtBytes(pendingFile.size)}</div>
            </div>
            <button onClick={handleCancelPending} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
              <FiX size={16} />
            </button>
          </div>
          <input
            type="text"
            placeholder="Caption (optional, e.g. 'Site plan from architect')"
            value={pendingCaption}
            onChange={(e) => setPendingCaption(e.target.value)}
            disabled={uploading}
            style={{
              width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
              borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '8px',
            }}
          />
          {hasPhases && (
            <div style={{ marginBottom: '8px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '3px' }}>
                Which phase can see this?
              </label>
              <select
                value={pendingPhaseTag}
                onChange={(e) => setPendingPhaseTag(e.target.value)}
                disabled={uploading}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
              >
                <option value="">Shared — all phases</option>
                {phases.map(p => <option key={p.id} value={String(p.id)}>{p.name || p.kind}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={handleUpload}
              disabled={uploading}
              style={{
                padding: '8px 14px', backgroundColor: '#16a34a', color: 'white',
                border: 'none', borderRadius: '6px', cursor: uploading ? 'wait' : 'pointer',
                fontSize: '13px', fontWeight: 600,
              }}
            >
              {uploading ? `Uploading... ${uploadProgress}%` : 'Upload'}
            </button>
            <button
              onClick={handleCancelPending}
              disabled={uploading}
              style={{
                padding: '8px 14px', backgroundColor: 'white', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
              }}
            >
              Cancel
            </button>
            {uploading && (
              <div style={{ flex: 1, height: '6px', background: '#dbeafe', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#2563eb', transition: 'width 0.2s' }} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ padding: '16px 0', color: '#9ca3af', fontSize: '13px' }}>Loading...</div>
      ) : attachments.length === 0 ? (
        <div style={{ padding: '20px 0', color: '#9ca3af', fontSize: '13px', textAlign: 'center' }}>
          No files attached yet. Use "Add File" to upload plans, PDFs, or photos.
        </div>
      ) : (
        <div>
          {attachments.map(att => (
            <div
              key={att.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', marginBottom: '6px',
                border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white',
              }}
            >
              <div style={{ flexShrink: 0 }}>{iconFor(att)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    onClick={() => openView(att)}
                    style={{
                      fontSize: '14px', fontWeight: 500, color: '#2563eb',
                      cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={att.original_filename}
                  >
                    {att.original_filename || 'Untitled'}
                  </div>
                  {att.collaborator_uploaded && (att.uploader_role === 'client' ? (
                    <span title={`Uploaded by the client through the portal${att.uploaded_by_name ? ` (${att.uploaded_by_name})` : ''}`} style={{
                      flexShrink: 0, fontSize: '10px', fontWeight: 700, letterSpacing: '0.3px',
                      textTransform: 'uppercase', padding: '2px 7px', borderRadius: '999px',
                      backgroundColor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0',
                    }}>
                      From client
                    </span>
                  ) : (
                    <span style={{
                      flexShrink: 0, fontSize: '10px', fontWeight: 700, letterSpacing: '0.3px',
                      textTransform: 'uppercase', padding: '2px 7px', borderRadius: '999px',
                      backgroundColor: '#f3e8ff', color: '#7c3aed', border: '1px solid #e9d5ff',
                    }}>
                      From sub
                    </span>
                  ))}
                  {hasPhases && (() => {
                    const kind = phaseKind(att.phase_tag);
                    return (
                      <span style={{
                        flexShrink: 0, fontSize: '10px', fontWeight: 700, letterSpacing: '0.3px',
                        textTransform: 'uppercase', padding: '2px 7px', borderRadius: '999px',
                        border: '1px solid',
                        backgroundColor: kind === 'design' ? '#eef2ff' : kind === 'construction' ? '#ecfdf5' : '#f1f5f9',
                        borderColor: kind === 'design' ? '#c7d2fe' : kind === 'construction' ? '#a7f3d0' : '#e2e8f0',
                        color: kind === 'design' ? '#4338ca' : kind === 'construction' ? '#047857' : '#64748b',
                      }}>
                        {phaseLabel(att.phase_tag)}
                      </span>
                    );
                  })()}
                </div>
                {editingCaptionId === att.id ? (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={editingCaptionVal}
                      onChange={(e) => setEditingCaptionVal(e.target.value)}
                      autoFocus
                      style={{ flex: 1, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                    />
                    <button
                      onClick={() => saveCaption(att)}
                      style={{ padding: '4px 8px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center' }}
                    >
                      <FiCheck size={12} />
                    </button>
                    <button
                      onClick={() => setEditingCaptionId(null)}
                      style={{ padding: '4px 8px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center' }}
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {att.caption && (
                      <span style={{ color: '#374151' }}>{att.caption}</span>
                    )}
                    <span>{fmtBytes(att.file_size)}</span>
                    {att.uploaded_by_name && <span>· by {att.uploaded_by_name}</span>}
                    {att.created_at && <span>· {new Date(att.created_at + 'Z').toLocaleDateString()}</span>}
                    {canEditCaption(att) && (
                      <button
                        onClick={() => startEditCaption(att)}
                        title="Edit caption"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'inline-flex' }}
                      >
                        <FiEdit2 size={11} />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => toggleClientVisible(att)}
                        title={att.client_visible ? 'Shared on client portal — click to hide' : 'Not shared — click to share on client portal'}
                        style={{
                          fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px',
                          cursor: 'pointer',
                          border: `1px solid ${att.client_visible ? '#86efac' : '#d1d5db'}`,
                          backgroundColor: att.client_visible ? '#dcfce7' : '#f9fafb',
                          color: att.client_visible ? '#166534' : '#6b7280',
                        }}
                      >
                        {att.client_visible ? '✓ Shared with client' : 'Share with client'}
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => toggleCollaboratorVisible(att)}
                        title={att.collaborator_visible
                          ? 'Visible to subcontractors/collaborators on this job — click to hide'
                          : 'Not visible to subcontractors — click to share with collaborators (engineers, architects)'}
                        style={{
                          fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px',
                          cursor: 'pointer',
                          border: `1px solid ${att.collaborator_visible ? '#93c5fd' : '#d1d5db'}`,
                          backgroundColor: att.collaborator_visible ? '#dbeafe' : '#f9fafb',
                          color: att.collaborator_visible ? '#1d4ed8' : '#6b7280',
                        }}
                      >
                        {att.collaborator_visible ? '✓ Shared with subs' : 'Share with subs'}
                      </button>
                    )}
                    {isAdmin && hasPhases && (
                      <select
                        value={(!att.phase_tag || att.phase_tag === 'shared') ? '' : String(att.phase_tag)}
                        onChange={(e) => changePhase(att, e.target.value)}
                        title="Which phase this document belongs to"
                        style={{
                          fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px',
                          border: '1px solid #d1d5db', backgroundColor: '#f9fafb', color: '#475569', cursor: 'pointer',
                        }}
                      >
                        <option value="">Shared — all phases</option>
                        {phases.map(p => <option key={p.id} value={String(p.id)}>{p.name || p.kind}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                {(isImage(att.mime_type, att.original_filename) || isPdf(att.mime_type, att.original_filename)) && (
                  <button
                    onClick={() => openView(att)}
                    title="View"
                    style={{
                      padding: '6px 8px', backgroundColor: '#eff6ff', color: '#2563eb',
                      border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <FiExternalLink size={14} />
                  </button>
                )}
                <button
                  onClick={() => handleDownload(att)}
                  title="Download"
                  style={{
                    padding: '6px 8px', backgroundColor: 'white', color: '#374151',
                    border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  <FiDownload size={14} />
                </button>
                {isAdmin && (
                  <button
                    onClick={() => handleDelete(att)}
                    title="Delete (admin only)"
                    style={{
                      padding: '6px 8px', backgroundColor: 'white', color: '#dc2626',
                      border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {showEmail && (
        <EmailToClientModal
          jobId={jobId}
          items={attachments.map(a => ({
            id: a.id,
            kind: 'attachment',
            name: a.original_filename || `file-${a.id}`,
            mime: a.mime_type || 'application/octet-stream',
            thumbUrl: isImage(a.mime_type, a.original_filename) ? `${API_BASE_URL || ''}${a.view_url}?token=${encodeURIComponent(token)}` : null,
            size: a.file_size,
            caption: a.caption,
          }))}
          onClose={() => { setShowEmail(false); load(); }}
        />
      )}
    </div>
  );
}

export default JobAttachments;
